import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Printer, Download, ArrowLeft, Trophy, FileDown, Loader2 } from "lucide-react";
import html2pdf from "html2pdf.js";

const PB_LOGO = "/brand/pb-logo.png";
const FMT = { single_elim: "Single Elimination", double_elim: "Double Elimination", round_robin: "Round Robin", pool_play: "Pool Play" };

const PRINT_CSS = `
@media print {
  body * { visibility: hidden; }
  #tourney-report, #tourney-report * { visibility: visible; }
  #tourney-report { position: absolute; inset: 0; background: #fff !important; color: #111 !important; padding: 0 !important; }
  #tourney-report .no-print { display: none !important; }
  #tourney-report table, #tourney-report th, #tourney-report td, #tourney-report div, #tourney-report span, #tourney-report h1, #tourney-report h2 { color: #111 !important; border-color: #999 !important; background: #fff !important; }
  #tourney-report .rpt-section { break-inside: avoid; }
}`;

export default function TournamentReport() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    api.get(`/tournaments/${id}`).then((r) => {
      setData(r.data);
      document.title = `${r.data.tournament.name} — Tournament Report`;
    }).catch(() => setError("Report unavailable — are you signed in?"));
  }, [id]);

  const model = useMemo(() => {
    if (!data) return null;
    const { tournament: t, entries, matches, divisions } = data;
    const nameOf = (eid) => !eid ? "TBD" : eid === "BYE" ? "BYE" : (entries.find((e) => e.id === eid)?.display_name || "TBD");
    const firstEv = divisions[0]?.event_type;
    const inDiv = (m, d) => (m.event_type || firstEv) === d.event_type && ((m.age_bracket || null) === (d.age_bracket || null)) && (!d.division || (m.division || null) === d.division);
    const sections = divisions.filter((d) => matches.some((m) => inDiv(m, d))).map((d) => {
      const ms = matches.filter((m) => inDiv(m, d));
      const done = ms.filter((m) => m.status === "complete");
      const stats = {};
      done.forEach((m) => {
        [[m.side_a, m.score_a, m.score_b], [m.side_b, m.score_b, m.score_a]].forEach(([eid, pf, pa]) => {
          if (!eid || eid === "BYE") return;
          const s = stats[eid] = stats[eid] || { w: 0, l: 0, pf: 0, pa: 0 };
          s.pf += pf || 0; s.pa += pa || 0;
          m.winner_id === eid ? s.w++ : s.l++;
        });
      });
      const standings = Object.entries(stats)
        .map(([eid, s]) => ({ team: nameOf(eid), ...s, diff: s.pf - s.pa }))
        .sort((a, b) => b.w - a.w || b.diff - a.diff || b.pf - a.pf);
      const champs = (t.champions || {})[d.label] || {};
      return { d, ms, standings, champs };
    });
    return { t, nameOf, sections, entries, matches };
  }, [data]);

  const [pdfBusy, setPdfBusy] = useState(false);

  const downloadPdf = async () => {
    setPdfBusy(true);
    const clone = document.getElementById("tourney-report").cloneNode(true);
    clone.querySelectorAll(".no-print").forEach((n) => n.remove());
    clone.id = "tourney-report-pdf";
    const wrap = document.createElement("div");
    wrap.style.cssText = "position:fixed;left:-10000px;top:0;width:794px;background:#fff;padding:24px;";
    const style = document.createElement("style");
    style.textContent = "#tourney-report-pdf, #tourney-report-pdf * { color:#111 !important; background:#fff !important; border-color:#999 !important; } #tourney-report-pdf .rpt-section { break-inside: avoid; }";
    wrap.appendChild(style);
    wrap.appendChild(clone);
    document.body.appendChild(wrap);
    try {
      await html2pdf().set({
        margin: [10, 8],
        filename: `${model.t.name.replace(/[^a-z0-9]+/gi, "_")}_report.pdf`,
        image: { type: "jpeg", quality: 0.95 },
        html2canvas: { scale: 2, backgroundColor: "#ffffff" },
        jsPDF: { unit: "mm", format: "a4", orientation: "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      }).from(clone).save();
    } finally {
      document.body.removeChild(wrap);
      setPdfBusy(false);
    }
  };

  const downloadCsv = () => {
    const rows = [["Division", "Match", "Court", "Team A", "Team B", "Score", "Winner", "Status"]];
    model.sections.forEach(({ d, ms }) => ms.forEach((m) => rows.push([
      d.label, m.key, m.court || "", model.nameOf(m.side_a), model.nameOf(m.side_b),
      m.status === "complete" ? `${m.score_a}-${m.score_b}` : "", m.status === "complete" ? model.nameOf(m.winner_id) : "", m.status])));
    const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `${model.t.name.replace(/[^a-z0-9]+/gi, "_")}_report.csv`;
    a.click();
  };

  if (error) return <div className="min-h-[60vh] flex items-center justify-center text-zinc-500 font-display uppercase">{error}</div>;
  if (!model) return <div className="min-h-[60vh] flex items-center justify-center text-zinc-600 animate-pulse">Building report…</div>;
  const { t, nameOf, sections, entries, matches } = model;
  const doneCount = matches.filter((m) => m.status === "complete").length;

  return (
    <div id="tourney-report" data-testid="tournament-report-page">
      <style>{PRINT_CSS}</style>
      <div className="no-print flex flex-wrap items-center gap-2 mb-5">
        <Link to={`/tournaments/${id}`} data-testid="report-back-link"
          className="flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-zinc-500 hover:text-white transition-colors mr-auto">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Tournament
        </Link>
        <button data-testid="report-print-btn" onClick={() => window.print()}
          className="flex items-center gap-1.5 bg-pb-cyan text-black text-[10px] font-black uppercase tracking-widest px-4 py-2.5 hover:opacity-90">
          <Printer className="w-4 h-4" /> Print
        </button>
        <button data-testid="report-pdf-btn" onClick={downloadPdf} disabled={pdfBusy}
          className="flex items-center gap-1.5 bg-pb-red text-black text-[10px] font-black uppercase tracking-widest px-4 py-2.5 hover:opacity-90 disabled:opacity-50">
          {pdfBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />} {pdfBusy ? "Building PDF…" : "Save PDF"}
        </button>
        <button data-testid="report-csv-btn" onClick={downloadCsv}
          className="flex items-center gap-1.5 border border-pb-cyan/60 text-pb-cyan text-[10px] font-black uppercase tracking-widest px-4 py-2.5 hover:bg-pb-cyan/10">
          <Download className="w-4 h-4" /> Download CSV
        </button>
      </div>

      <div className="rpt-section border-b-2 border-zinc-700 pb-4 mb-6 flex items-start gap-4">
        <img src={PB_LOGO} alt="" className="w-14 h-14 object-contain no-print" />
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.3em] text-pb-cyan">Official Tournament Report · Pickle Bullpen</div>
          <h1 className="font-display font-black uppercase text-2xl sm:text-3xl leading-tight" data-testid="report-title">{t.name}</h1>
          <div className="text-xs text-zinc-400 mt-1 flex flex-wrap gap-x-4 gap-y-0.5">
            <span>{t.date}{t.end_date ? ` → ${t.end_date}` : ""}</span>
            {t.location && <span>{t.location}</span>}
            <span>{FMT[t.format] || t.format}</span>
            <span>Director: {t.director_name || "—"}</span>
            <span>Courts: {(t.courts || []).join(", ") || "—"}</span>
            <span>{entries.length} entries</span>
            <span>{doneCount}/{matches.length} matches played</span>
            <span className="uppercase font-bold">{t.status === "complete" ? "FINAL" : t.status}</span>
          </div>
        </div>
      </div>

      {t.psi_movers?.top3?.length > 0 && (
        <section className="rpt-section mb-8" data-testid="report-psi-movers">
          <h2 className="font-display font-black uppercase text-lg text-amber-300 flex items-center gap-2 mb-2"><Trophy className="w-4 h-4" /> Top 3 PSI Movers</h2>
          <table className="w-full text-xs border border-zinc-700">
            <thead><tr className="text-left text-[10px] uppercase tracking-widest text-zinc-500 bg-zinc-900"><th className="px-2 py-1.5">#</th><th className="px-2 py-1.5">Player</th><th className="px-2 py-1.5 text-right">Start PSI</th><th className="px-2 py-1.5 text-right">Final PSI</th><th className="px-2 py-1.5 text-right">Movement</th><th className="px-2 py-1.5 text-right">Matches</th></tr></thead>
            <tbody>
              {t.psi_movers.top3.map((r, i) => (
                <tr key={r.user_id} className="border-t border-zinc-800" data-testid={`report-psi-mover-${i + 1}`}>
                  <td className="px-2 py-1.5">{["🥇", "🥈", "🥉"][i]}</td><td className="px-2 py-1.5 font-bold">{r.name}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{r.start_psi}</td><td className="px-2 py-1.5 text-right font-mono">{r.current_psi}</td>
                  <td className={`px-2 py-1.5 text-right font-mono font-black ${r.delta >= 0 ? "text-emerald-300" : "text-pb-red"}`}>{r.delta >= 0 ? "+" : ""}{r.delta.toFixed(1)}</td>
                  <td className="px-2 py-1.5 text-right font-mono">{r.matches}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      {sections.map(({ d, ms, standings, champs }) => (
        <section key={d.label} className="rpt-section mb-8" data-testid={`report-division-${d.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
          <h2 className="font-display font-black uppercase text-lg border-b border-zinc-700 pb-1 mb-3 flex items-center gap-2">
            {d.label}
            {(champs.champion_name) && <span className="text-xs font-bold text-yellow-400 flex items-center gap-1"><Trophy className="w-3.5 h-3.5" /> {champs.champion_name}</span>}
          </h2>
          {(champs.champion_name || champs.runner_up_name || champs.third_name) && (
            <div className="text-xs mb-3 space-y-0.5">
              {champs.champion_name && <div>🥇 <b>Gold:</b> {champs.champion_name}</div>}
              {champs.runner_up_name && <div>🥈 <b>Silver:</b> {champs.runner_up_name}</div>}
              {champs.third_name && <div>🥉 <b>Bronze:</b> {champs.third_name}</div>}
            </div>
          )}
          {standings.length > 0 && (
            <table className="w-full text-xs border border-zinc-700 mb-3">
              <thead><tr className="text-left uppercase tracking-widest text-[9px] text-zinc-400 border-b border-zinc-700">
                <th className="px-2 py-1.5">#</th><th className="px-2 py-1.5">Team</th><th className="px-2 py-1.5">W</th><th className="px-2 py-1.5">L</th><th className="px-2 py-1.5">PF</th><th className="px-2 py-1.5">PA</th><th className="px-2 py-1.5">+/−</th>
              </tr></thead>
              <tbody>
                {standings.map((s, i) => (
                  <tr key={s.team} className="border-b border-zinc-800">
                    <td className="px-2 py-1">{i + 1}</td><td className="px-2 py-1 font-semibold">{s.team}</td>
                    <td className="px-2 py-1">{s.w}</td><td className="px-2 py-1">{s.l}</td>
                    <td className="px-2 py-1">{s.pf}</td><td className="px-2 py-1">{s.pa}</td>
                    <td className="px-2 py-1">{s.diff > 0 ? `+${s.diff}` : s.diff}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <table className="w-full text-xs border border-zinc-700">
            <thead><tr className="text-left uppercase tracking-widest text-[9px] text-zinc-400 border-b border-zinc-700">
              <th className="px-2 py-1.5">Match</th><th className="px-2 py-1.5">Court</th><th className="px-2 py-1.5">Matchup</th><th className="px-2 py-1.5">Score</th><th className="px-2 py-1.5">Winner</th>
            </tr></thead>
            <tbody>
              {ms.map((m) => (
                <tr key={m.key} className="border-b border-zinc-800" data-testid={`report-match-${m.key}`}>
                  <td className="px-2 py-1 font-mono text-[10px]">{m.key}{m.key.endsWith("BRZ") ? " (Bronze)" : ""}</td>
                  <td className="px-2 py-1">{m.court || "—"}</td>
                  <td className="px-2 py-1">{nameOf(m.side_a)} <span className="text-zinc-500">vs</span> {nameOf(m.side_b)}</td>
                  <td className="px-2 py-1 font-mono font-bold">{m.status === "complete" ? `${m.score_a}–${m.score_b}` : "—"}</td>
                  <td className="px-2 py-1">{m.status === "complete" ? nameOf(m.winner_id) : m.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}

      <div className="rpt-section text-[10px] text-zinc-500 border-t border-zinc-700 pt-2">
        Generated {new Date().toLocaleString()} · Pickle Bullpen Performance OS · picklebullpen.com
      </div>
    </div>
  );
}
